'use client';

/**
 * useMarketPage — hook con TODA la lógica de state + fetch + handlers para
 * la página pública `/market` (Fase 3 Task D1 — catálogo de productos).
 *
 * V124.1-F1 split (per `docs/AT-RISK-FILES-AUDIT-v124.1.md` §2.1
 *  MONITOREAR — `market/page.tsx` 958 L, -42 L del umbral). Patrón espejo
 * de PR #1365 (`market/cupones/hooks/use-cupones-page.ts`).
 *
 * SPEC-244 §4 Estrategia A — VERBATIM: los cuerpos de useState/useEffect/
 * useCallback/useMemo son byte-exact al pre-split (lines 200-439 de
 * `page.tsx` pre-split).
 *
 * Spec ref: SPEC-081 Fase 3 §6.1 + §4.1 + §3.2, SPEC-NEGOCIO §10.4 +
 *           §10.11, ADR-039 + enmienda INV-MKT-21. Sprint 138 PR-C.2 —
 *           geo filter (Local/Nacional/Internacional/Todos) con
 *           persistencia en localStorage (INV-MKTFILTER-10).
 *
 * Anti-leak de setters: las 3 llamadas inline `setFilters((prev) => ...)`
 * del JSX pre-split (geo tab onClick line 552, LocationSelector
 * onLocationChange line 575, "Reintentar" button onClick line 828) se
 * exponen como handlers explícitos (`onGeoTabChange`, `onLocationChange`,
 * `onRetry`) para que `setFilters` no se filtre fuera del hook.
 *
 * Anti-P-041 (0 `as any`), Anti-P-042 (0 `console.*`),
 * Anti-P-382 (page.tsx se reduce — NO coexiste la lógica duplicada),
 * Anti-P-383 (archivo ≤500 L — objetivo 360).
 * RT-1: solo se agrega este archivo nuevo + se edita `page.tsx`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type * as React from 'react';
import { api, ApiClientError } from '@/lib/api-client';
import { setReferralCookie } from '@/lib/referral-cookie';
// TASK-FEAT-MARKET-CATEGORIES-DYNAMIC: fetch categories from the API (DB
// with fallback to the constant). Replaces the direct MARKET_CATEGORIES
// import — the constant is now the SSR-safe initial state inside the
// useMarketCategories hook (fail-open fallback if API fails).
import { useMarketCategories } from './use-market-categories';
import type {
  ProductListResponse,
  ProductListFilters,
  ProductType,
  // Sprint 138 PR-C.2 — geo filter type for the Tabs UI.
  MarketplaceGeoFilter,
} from '@profileia/shared';

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Page size for the catalog grid. 12 = 4 cols × 3 rows on desktop, 2 cols
 * × 6 rows on tablet, 1 col × 12 rows on mobile. The backend clamps
 * `limit` to `[1, 60]` (defense in depth) so 12 is well within range.
 */
const PAGE_SIZE = 12;

/**
 * Search input debounce (ms). 400ms balances responsiveness with API
 * load — typical type-ahead debounce per Nielsen Norman Group guidance.
 */
const SEARCH_DEBOUNCE_MS = 400;

/**
 * Sort options — verbatim from `ProductListFilters.sortBy` (Task A —
 * `types/product.ts`). The labels are UX affordances; the backend
 * independently maps the `sortBy` string to the MongoDB sort spec.
 *
 * NOTE: `ProductListFilters['sortBy']` is optional (`sortBy?: ...`) so we
 * extract the non-optional union here to avoid `string | undefined` typing
 * issues when binding to `<SelectItem value={opt.value}>` (which requires
 * a `string`).
 */
type SortBy = NonNullable<ProductListFilters['sortBy']>;
export const SORT_OPTIONS: Array<{ value: SortBy; label: string }> = [
  // Feature: random sort por defecto — los primeros productos no se pierden.
  // Pre-fix: default era 'newest' lo que hacía que los productos más viejos
  // nunca aparecieran arriba. 'random' usa MongoDB $sample para mezclar.
  { value: 'random', label: 'Aleatorio' },
  { value: 'newest', label: 'Más recientes' },
  { value: 'price_low', label: 'Precio: menor a mayor' },
  { value: 'price_high', label: 'Precio: mayor a menor' },
  { value: 'rating', label: 'Mejor calificados' },
];

/**
 * Type filter options — `ProductType` (Task A) + an "all" option. The
 * "all" value is the empty string (sentinel) — when selected, the
 * `type` filter is omitted from the API call (handled in `buildFilters`).
 */
export const TYPE_OPTIONS: Array<{ value: '' | ProductType; label: string }> = [
  { value: '', label: 'Todos los tipos' },
  { value: 'physical', label: 'Físico' },
  { value: 'digital', label: 'Digital' },
  { value: 'service', label: 'Servicio' },
];

// ─── Local state shape ──────────────────────────────────────────────────────

/**
 * Catalog filter state — mirrors `ProductListFilters` (Task A) but with
 * looser typing for the form inputs (empty strings instead of `undefined`
 * so controlled inputs work cleanly). Converted to `ProductListFilters`
 * via `buildFilters` before the API call.
 */
export interface CatalogFilters {
  search: string;
  category: string;     // '' = all
  subcategory: string;  // '' = all
  type: '' | ProductType;
  minPrice: string;     // '' = no min
  maxPrice: string;     // '' = no max
  sortBy: SortBy;
  page: number;
  // Sprint 138 (PR-C.2 — Feature 4, INV-MKTFILTER-01..10): filtro geográfico.
  // 'local' = sellers en la ciudad del comprador (requires userCityId)
  // 'national' = sellers en el país del comprador (requires userCountryIso)
  // 'international' = sellers con ships=internacional al país del comprador
  // 'all' = sin filtro geográfico
  geo: '' | MarketplaceGeoFilter;  // '' = all (default — backward compat)
  // Sprint 138 (PR-C.2): país ISO alpha-2 del comprador (cuando no está
  // logueado, el frontend lo infiere desde Accept-Language header).
  userCountryIso: string;  // '' = unknown
  // Sprint 138 (PR-C.2): city ID (GeoLocation UUID) del comprador logueado.
  // '' = unknown (el frontend lo obtiene del /api/auth/me response).
  userCityId: string;  // '' = unknown
}

export const DEFAULT_FILTERS: CatalogFilters = {
  search: '',
  category: '',
  subcategory: '',
  type: '',
  minPrice: '',
  maxPrice: '',
  sortBy: 'random',
  page: 1,
  // Sprint 138 PR-C.2 — default geo = '' (all — no filter). The user can
  // click a Tab to select Local/Nacional/Internacional.
  geo: '',
  userCountryIso: '',
  userCityId: '',
};

// ─── Hook return shape ───────────────────────────────────────────────────────

export interface UseMarketPageReturn {
  // State
  filters: CatalogFilters;
  loading: boolean;
  loadError: string | null;
  data: ProductListResponse | null;
  searchInput: string;
  // State setters (for the input + filter UI)
  setSearchInput: (value: string) => void;
  // Filter handlers
  onSortChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onSubcategoryChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onMinPriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMaxPriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPriceRangeChange: (min: string, max: string) => void;
  onClearFilters: () => void;
  onPageChange: (page: number) => void;
  // V124.1-F1: `onRetry` re-dispara el fetch (usado por el botón "Reintentar"
  // cuando `loadError` está seteado). El botón pre-split usaba
  // `setFilters((prev) => ({ ...prev }))` — lo expusimos como handler
  // explícito para no filtrar `setFilters` fuera del hook.
  onRetry: () => void;
  // V124.1-F1: `onGeoTabChange` maneja el click en los Tabs de filtro
  // geográfico (Local/Nacional/Internacional/Todos). Pre-split era un
  // inline `onClick={() => setFilters(...) + localStorage.setItem(...)}`.
  onGeoTabChange: (tab: MarketplaceGeoFilter) => void;
  // V124.1-F1: `onLocationChange` maneja el callback de LocationSelector.
  // Pre-split era un inline `onLocationChange={(loc) => setFilters(...)}`.
  onLocationChange: (loc: { countryIso: string; cityId: string }) => void;
  // Derived
  subcategories: string[];
  totalPages: number;
  currentPage: number;
  hasActiveFilters: boolean;
  paginationItems: Array<{ page: number; label: string }>;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * useMarketPage — hook con toda la lógica de state + fetch + handlers
 * para la página pública `/market`.
 *
 * El hook es "headless" — retorna state + setters + handlers. La página
 * consume los valores + renderiza el JSX.
 */
export function useMarketPage(): UseMarketPageReturn {
  // Sprint 138 PR-C.2 — initialize geo filter from localStorage if present
  // (INV-MKTFILTER-10). Only run on client-side (Next.js SSR safe — the
  // initial useState is server-rendered with DEFAULT_FILTERS, then the
  // effect overrides after mount).
  const [filters, setFilters] = useState<CatalogFilters>(() => {
    if (typeof window === 'undefined') return DEFAULT_FILTERS;
    try {
      const saved = localStorage.getItem('profileia:marketplace-filter');
      if (saved === 'local' || saved === 'national' || saved === 'international' || saved === 'all') {
        return { ...DEFAULT_FILTERS, geo: saved };
      }
    } catch {
      // localStorage unavailable (private mode) — use default.
    }
    return DEFAULT_FILTERS;
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [data, setData] = useState<ProductListResponse | null>(null);

  // Debounced search value — when the user types in the search input,
  // we wait SEARCH_DEBOUNCE_MS before updating `filters.search` (which
  // triggers the fetch effect). This avoids one API call per keystroke.
  const [searchInput, setSearchInput] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // SPEC-094 §1.2 (Cookie de Referido Extendida): capture `?ref=USERNAME`
  // from the URL on mount + set the `profileia_ref` cookie (30 días,
  // SameSite=Lax, Secure si HTTPS — same cookie used by /r/[code]).
  // The cookie persists across browser sessions so when the visitor
  // eventually registers (with or without ?ref= in the URL), the backend
  // attributes the registration to this referral code (SPEC-NEGOCIO §8.3).
  //
  // SSR-safe: `window` is undefined on the server — the useEffect only
  // runs on the client. Using `window.location.search` (instead of
  // `useSearchParams`) avoids the Suspense boundary requirement that
  // `useSearchParams` triggers on statically-rendered pages (this page
  // is a `'use client'` component, but `useSearchParams` still triggers
  // a Next.js build-time warning without a Suspense boundary).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferralCookie(ref);
  }, []);

  // ── SPEC-MARKETPLACE-GEO-FILTER-V2: detect buyer geolocation ──
  // Populates userCountryIso + userCityId so the Local/Nacional/Internacional
  // filters work. Priority: localStorage (manual override) → Accept-Language
  // (country from browser). cityId requires the user to be logged in with
  // KYC — TODO: wire /api/auth/me to expose KYC city UUID.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 1. Check localStorage for a manual override.
    try {
      const savedLocation = localStorage.getItem('profileia:buyer-location');
      if (savedLocation) {
        const parsed = JSON.parse(savedLocation) as { countryIso?: string; cityId?: string };
        if (parsed.countryIso || parsed.cityId) {
          setFilters((prev) => ({
            ...prev,
            userCountryIso: parsed.countryIso ?? '',
            userCityId: parsed.cityId ?? '',
          }));
          return;
        }
      }
    } catch {
      // localStorage unavailable or invalid JSON — continue to next method.
    }

    // 2. Fallback: detect country from the browser's language setting.
    // navigator.language returns 'es-CO', 'en-US', etc.
    // The country code is the part after the '-'.
    const lang = navigator.language || (navigator.languages?.[0] ?? '');
    if (lang) {
      const parts = lang.split('-');
      if (parts.length >= 2) {
        const countryIso = parts[1].toUpperCase();
        setFilters((prev) => ({
          ...prev,
          userCountryIso: countryIso,
        }));
      }
    }
  }, []);

  // ── Debounced search → filters.search ──
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      // Only update filters.search if it actually changed — avoids an
      // extra render + fetch when the user pastes the same value.
      setFilters((prev) =>
        prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchInput]);

  // ── Build the ProductListFilters from the local CatalogFilters ──
  const buildFilters = useCallback((): ProductListFilters => {
    const f: ProductListFilters = {
      page: filters.page,
      limit: PAGE_SIZE,
      sortBy: filters.sortBy,
    };
    if (filters.search.trim()) f.search = filters.search.trim();
    if (filters.category) f.category = filters.category;
    if (filters.subcategory) f.subcategory = filters.subcategory;
    if (filters.type) f.type = filters.type;
    if (filters.minPrice) f.minPrice = filters.minPrice;
    if (filters.maxPrice) f.maxPrice = filters.maxPrice;
    // Sprint 138 PR-C.2 — pass geo + userCountryIso + userCityId to the backend.
    // Only pass them when the geo filter is selected (not '' default).
    // The backend service handles empty userCountryIso/userCityId by returning
    // an empty list for local/national/international queries (INV-MKTFILTER-04).
    if (filters.geo) f.geo = filters.geo;
    if (filters.userCountryIso) f.userCountryIso = filters.userCountryIso;
    if (filters.userCityId) f.userCityId = filters.userCityId;
    return f;
  }, [filters]);

  // ── Fetch products on filters change (AbortController per P-043) ──
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    api.market
      .listProducts(buildFilters(), controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiClientError) {
          if (err.status === 403) {
            setLoadError(
              'Profileia Market (productos) está temporalmente deshabilitado. Vuelve más tarde.',
            );
          } else {
            setLoadError(err.message || 'Error al cargar el catálogo.');
          }
        } else {
          setLoadError('Error de red al cargar el catálogo. Verifica tu conexión.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [buildFilters]);

  // ── Filter handlers ──
  const onCategoryChange = useCallback((value: string) => {
    // 'all' sentinel → '' (no filter). Subcategory resets when category
    // changes — the subcategory list is category-dependent.
    const next = value === 'all' ? '' : value;
    setFilters((prev) => ({ ...prev, category: next, subcategory: '', page: 1 }));
  }, []);

  const onSubcategoryChange = useCallback((value: string) => {
    const next = value === 'all' ? '' : value;
    setFilters((prev) => ({ ...prev, subcategory: next, page: 1 }));
  }, []);

  const onTypeChange = useCallback((value: string) => {
    const next = (value === '' ? '' : value) as CatalogFilters['type'];
    setFilters((prev) => ({ ...prev, type: next, page: 1 }));
  }, []);

  const onSortChange = useCallback((value: string) => {
    const next = value as SortBy;
    setFilters((prev) => ({ ...prev, sortBy: next, page: 1 }));
  }, []);

  const onMinPriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, minPrice: e.target.value, page: 1 }));
  }, []);

  const onMaxPriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, maxPrice: e.target.value, page: 1 }));
  }, []);

  // M-8 (Sprint 4): dual-thumb slider callback — updates both boundaries in
  // one tick (the slider emits both thumbs together). The fallback number
  // inputs above remain wired to onMinPriceChange / onMaxPriceChange for
  // precise entry + values outside the 0-1000 slider range.
  const onPriceRangeChange = useCallback((min: string, max: string) => {
    setFilters((prev) => ({ ...prev, minPrice: min, maxPrice: max, page: 1 }));
  }, []);

  const onClearFilters = useCallback(() => {
    setSearchInput('');
    setFilters(DEFAULT_FILTERS);
  }, []);

  const onPageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
    // Scroll to top of grid on page change — better UX than staying at
    // the bottom of the previous page's grid.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // V124.1-F1: re-dispara el fetch cuando el usuario hace click en
  // "Reintentar" tras un loadError. Pre-split era
  // `setFilters((prev) => ({ ...prev }))` — forzamos una nueva ref del
  // objeto filters para que el useEffect de fetch (que tiene `buildFilters`
  // en sus deps, que a su vez depende de `filters`) se re-dispare.
  const onRetry = useCallback(() => {
    setFilters((prev) => ({ ...prev }));
  }, []);

  // V124.1-F1: maneja el click en los Tabs de filtro geográfico. Pre-split
  // era un inline `onClick={() => { setFilters(...); localStorage.setItem(...) }}`
  // en el JSX. Cuerpo byte-exact al pre-split (page.tsx lines 551-561).
  const onGeoTabChange = useCallback((tab: MarketplaceGeoFilter) => {
    setFilters((prev) => ({ ...prev, geo: tab, page: 1 }));
    // INV-MKTFILTER-10 — persist the filter in localStorage.
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('profileia:marketplace-filter', tab);
      } catch {
        // localStorage may throw in private mode — silently ignore.
      }
    }
  }, []);

  // V124.1-F1: maneja el callback de LocationSelector. Pre-split era un
  // inline `onLocationChange={(loc) => setFilters(...)}`. Cuerpo
  // byte-exact al pre-split (page.tsx line 575).
  const onLocationChange = useCallback((loc: { countryIso: string; cityId: string }) => {
    setFilters((prev) => ({ ...prev, userCountryIso: loc.countryIso, userCityId: loc.cityId, page: 1 }));
  }, []);

  // ── Derived: subcategories for the selected category ──
  // TASK-FEAT-MARKET-CATEGORIES-DYNAMIC: use the dynamic categories from
  // the API (DB with fallback to the constant). Initial state is the
  // constant for SSR; the API call updates if DB has categories.
  const marketCategories = useMarketCategories();
  const subcategories = useMemo(() => {
    if (!filters.category) return [] as string[];
    const cat = marketCategories.find((c) => c.category === filters.category);
    return cat?.subcategories ?? [];
  }, [filters.category, marketCategories]);

  // ── Derived: pagination metadata ──
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const currentPage = data ? data.page : 1;
  const hasActiveFilters =
    filters.search !== '' ||
    filters.category !== '' ||
    filters.subcategory !== '' ||
    filters.type !== '' ||
    filters.minPrice !== '' ||
    filters.maxPrice !== '' ||
    filters.geo !== '';

  // ── Render: pagination items (windowed — show ±2 pages around current) ──
  const paginationItems = useMemo(() => {
    const items: Array<{ page: number; label: string }> = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let p = start; p <= end; p++) {
      items.push({ page: p, label: String(p) });
    }
    return items;
  }, [currentPage, totalPages]);

  return {
    filters,
    loading,
    loadError,
    data,
    searchInput,
    setSearchInput,
    onSortChange,
    onCategoryChange,
    onSubcategoryChange,
    onTypeChange,
    onMinPriceChange,
    onMaxPriceChange,
    onPriceRangeChange,
    onClearFilters,
    onPageChange,
    onRetry,
    onGeoTabChange,
    onLocationChange,
    subcategories,
    totalPages,
    currentPage,
    hasActiveFilters,
    paginationItems,
  };
}
